import { FuelCurveInput, FuelCurveRow } from "../models/calculators.models";
import CargillLogo from "../../../shared/assets/CargillLogo.svg";
import CalculatorNumberInput from "./CalculatorNumberInput";
import FuelCurveTable from "./FuelCurveTable";

interface FuelCurveCalculatorProps {
  fuelCurve: FuelCurveInput;
  ladenRows: FuelCurveRow[];
  ballastRows: FuelCurveRow[];
  onChange: (fuelCurve: FuelCurveInput) => void;
}

type FuelCurveCondition = "laden" | "ballast";

const FuelCurveCalculator = ({ fuelCurve, ladenRows, ballastRows, onChange }: FuelCurveCalculatorProps) => {
  const updateExponent = (value: number | null) => onChange({ ...fuelCurve, speedExponent: value ?? 0 });
  const updateBasis = (
    condition: FuelCurveCondition,
    field: "referenceSpeed" | "meConsumption" | "aeConsumption",
    value: number | null,
  ) => {
    onChange({ ...fuelCurve, [condition]: { ...fuelCurve[condition], [field]: value ?? 0 } });
  };

  return (
    <section data-test="fuel-curve-calculator" className="min-w-0 p-5">
      <div className="grid grid-cols-[90px_1fr_90px] items-start">
        <CalculatorNumberInput label="Speed Exp" value={fuelCurve.speedExponent} dataTest="fuel-speed-exponent" onChange={updateExponent} />
        <h2 className="pt-1 text-center text-xl font-bold text-gray-950 dark:text-white">Fuel Curve Calculator</h2>
        <img src={CargillLogo} alt="Cargill" className="h-9 justify-self-end" />
      </div>

      <div className="mt-4 grid grid-cols-2 gap-7">
        {(["laden", "ballast"] as FuelCurveCondition[]).map((condition) => (
          <div key={condition} className="grid grid-cols-3 gap-3">
            <CalculatorNumberInput
              label={`${condition === "laden" ? "Laden" : "Ballast"} Speed`}
              value={fuelCurve[condition].referenceSpeed}
              dataTest={`fuel-${condition}-reference-speed`}
              onChange={(value) => updateBasis(condition, "referenceSpeed", value)}
            />
            <CalculatorNumberInput
              label={`${condition === "laden" ? "Laden" : "Ballast"} ME Cons`}
              value={fuelCurve[condition].meConsumption}
              dataTest={`fuel-${condition}-me-consumption`}
              onChange={(value) => updateBasis(condition, "meConsumption", value)}
            />
            <CalculatorNumberInput
              label={`${condition === "laden" ? "Laden" : "Ballast"} AE Cons`}
              value={fuelCurve[condition].aeConsumption}
              dataTest={`fuel-${condition}-ae-consumption`}
              onChange={(value) => updateBasis(condition, "aeConsumption", value)}
            />
          </div>
        ))}
      </div>

      <div className="mt-5 grid grid-cols-1 gap-7 sm:grid-cols-2">
        <FuelCurveTable title="Laden" rows={ladenRows} dataTest="fuel-laden-curve-table" highlightedSpeeds={[11.5, 13]} />
        <FuelCurveTable title="Ballast" rows={ballastRows} dataTest="fuel-ballast-curve-table" highlightedSpeeds={[12.5, 14]} />
      </div>
    </section>
  );
};

export default FuelCurveCalculator;
